import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortfolio, CreatePortfolioParams } from "api/student/v2/portfolio.api";
import { uploadVideo, uploadImage } from "api/common/Data.api";

const PortfolioCreate = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    
    const [title, setTitle] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [videoPreview, setVideoPreview] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ title?: string; video?: string }>({});

    // 영상 파일 선택 처리
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 파일 타입 검증
        if (!file.type.startsWith('video/')) {
            setErrors(prev => ({ ...prev, video: "영상 파일만 선택할 수 있습니다." }));
            return;
        }

        // 파일 크기 검증 (100MB 제한)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            setErrors(prev => ({ ...prev, video: "파일 크기는 100MB 이하여야 합니다." }));
            return;
        }

        setSelectedFile(file);
        setErrors(prev => ({ ...prev, video: "" }));

        // 영상 미리보기 생성
        const reader = new FileReader();
        reader.onload = (e) => {
            setVideoPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    // 파일 선택 버튼 클릭
    const handleSelectFile = () => {
        fileInputRef.current?.click();
    };

    // 영상 제목 검증
    const validateTitle = (value: string) => {
        if (!value.trim()) {
            setErrors(prev => ({ ...prev, title: "영상 제목을 입력해주세요." }));
            return false;
        }
        if (value.length > 100) {
            setErrors(prev => ({ ...prev, title: "제목은 100자 이하여야 합니다." }));
            return false;
        }
        setErrors(prev => ({ ...prev, title: "" }));
        return true;
    };

    // 제목 변경 처리
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setTitle(value);
        if (value) {
            validateTitle(value);
        } else {
            setErrors(prev => ({ ...prev, title: "" }));
        }
    };

    // 포트폴리오 업로드 처리
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // 검증
        if (!validateTitle(title)) return;
        if (!selectedFile) {
            setErrors(prev => ({ ...prev, video: "영상을 선택해주세요." }));
            return;
        }

        setIsLoading(true);

        try {
            // 1. 선택된 영상 파일에서 썸네일 이미지 추출
            const thumbnailBlob = await extractThumbnailFromVideo(selectedFile);
            
            // 2. 썸네일 이미지를 File 객체로 변환
            const thumbnailFile = new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
            
            // 3. 썸네일 이미지 업로드
            const thumbnailImage = await uploadImage(thumbnailFile, true);
            
            // 4. 영상 파일 업로드
            const video = await uploadVideo(selectedFile, false);
            
            // 5. 포트폴리오 생성
            const params: CreatePortfolioParams = {
                title,
                videoId: video.id,
                thumbnailImageId: thumbnailImage.id,
            };
            await createPortfolio(params);

            // 성공 시 포트폴리오 목록으로 이동
            navigate('/student/profile/portfolio');
        } catch (error) {
            console.error('포트폴리오 업로드 실패:', error);
            setErrors(prev => ({ ...prev, video: "업로드 중 오류가 발생했습니다." }));
        } finally {
            setIsLoading(false);
        }
    };

    // 영상에서 썸네일 추출
    const extractThumbnailFromVideo = (videoFile: File): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            video.onloadedmetadata = () => {
                // 영상의 1초 지점에서 썸네일 추출
                video.currentTime = 1;
            };
            
            video.onseeked = () => {
                if (ctx) {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('썸네일 추출 실패'));
                        }
                    }, 'image/jpeg', 0.8);
                } else {
                    reject(new Error('Canvas context 생성 실패'));
                }
            };
            
            video.onerror = () => {
                reject(new Error('영상 로드 실패'));
            };
            
            video.src = URL.createObjectURL(videoFile);
        });
    };

    // 뒤로가기
    const handleBack = () => {
        navigate('/student/profile/portfolio');
    };

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
                {/* 헤더 */}
                <div className="flex items-center mb-6 sm:mb-8">
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center text-gray-600 hover:text-gray-900 mr-3 sm:mr-4"
                    >
                        <svg
                            className="w-5 h-5 sm:w-6 sm:h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        새 포트폴리오 만들기
                    </h1>
                </div>

                <div className="max-w-2xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        {/* 영상 제목 입력 */}
                        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
                                영상 제목
                            </h2>
                            <div>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={handleTitleChange}
                                    placeholder="영상 제목을 입력해주세요"
                                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] transition-colors text-sm sm:text-base ${
                                        errors.title ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    maxLength={100}
                                />
                                {errors.title && (
                                    <p className="mt-2 text-sm text-red-600">{errors.title}</p>
                                )}
                                <p className="mt-2 text-xs sm:text-sm text-gray-500">
                                    {title.length}/100자
                                </p>
                            </div>
                        </div>

                        {/* 영상 업로드 및 미리보기 */}
                        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
                                영상 업로드
                            </h2>
                            
                            {/* 영상 미리보기 */}
                            <div className="mb-4 sm:mb-6">
                                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-3">
                                    영상 미리보기
                                </h3>
                                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                                    {videoPreview ? (
                                        <video
                                            ref={videoRef}
                                            src={videoPreview}
                                            className="w-full h-full object-contain"
                                            controls
                                            preload="metadata"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-center px-4">
                                                <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-sm sm:text-base text-gray-500">영상을 선택하면 미리보기가 표시됩니다</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 파일 선택 */}
                            <div>
                                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-3">
                                    영상 파일 선택
                                </h3>
                                <div className="space-y-3 sm:space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                                        <button
                                            type="button"
                                            onClick={handleSelectFile}
                                            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-[#CF3688] text-white rounded-lg hover:bg-[#B52E74] transition-colors font-medium text-sm sm:text-base"
                                        >
                                            영상 선택하기
                                        </button>
                                        {selectedFile && (
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-900 font-medium truncate">
                                                    {selectedFile.name}
                                                </p>
                                                <p className="text-xs sm:text-sm text-gray-500">
                                                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="video/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    
                                    {errors.video && (
                                        <p className="text-sm text-red-600">{errors.video}</p>
                                    )}
                                    
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                                        <h4 className="text-xs sm:text-sm font-medium text-blue-900 mb-2">
                                            업로드 가이드라인
                                        </h4>
                                        <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                                            <li>• 지원 형식: MP4, MOV, AVI, WMV</li>
                                            <li>• 최대 파일 크기: 100MB</li>
                                            <li>• 권장 해상도: 1920x1080 (Full HD) 이상</li>
                                            <li>• 권장 길이: 1분 ~ 10분</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 업로드 버튼 */}
                        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading || !title.trim() || !selectedFile}
                                className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                                    isLoading || !title.trim() || !selectedFile
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#CF3688] text-white hover:bg-[#B52E74]'
                                }`}
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>업로드 중...</span>
                                    </div>
                                ) : (
                                    '포트폴리오 등록'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    );
};

export default PortfolioCreate; 